import { TableColumnSortDirection } from '../../../../../../../components/Table';
import { Localizer } from '../../../../../../../locale/useTextLocalizer';
import { isNullish } from '@sas/nova-core/typeguards';
import { formatBoolean } from './format';

export const compare = (props: {
  formatLocale: string;
  value1: any;
  value2: any;
  caseSensitive?: boolean;
  trimStringValues?: boolean;
  localize?: Localizer;
  collator?: Intl.Collator;
}) => {
  let a = props.value1;
  let b = props.value2;
  const { caseSensitive, localize, formatLocale } = props;
  const trimStringValues = props.trimStringValues === undefined ? true : props.trimStringValues;

  // can we short-circuit this whole thing?
  if (a === b) return 0;

  // DATAPREP-18694 - undefined, null and blank strings should sort lower than anything else
  if (b === undefined) return 1;
  if (a === undefined) return -1;
  if (b === null) return 1;
  if (a === null) return -1;
  if (b === '') return 1;
  if (a === '') return -1;

  if (typeof a === 'string' && typeof b === 'string') {
    let collator: Intl.Collator | undefined = props.collator;
    if (!collator) {
      const options = {
        sensitivity: caseSensitive ? 'case' : 'base',
      };
      collator = new Intl.Collator(formatLocale, options);
    }
    if (trimStringValues) {
      a = a.trim();
      b = b.trim();
    }
    return collator.compare(a, b);
  }

  if (
    typeof a === 'object' &&
    typeof b === 'object' &&
    typeof a.value === 'number' &&
    typeof b.value === 'number'
  ) {
    a = a.value;
    b = b.value;
  }

  if (
    typeof a === 'object' &&
    typeof b === 'object' &&
    typeof a.value === 'boolean' &&
    typeof b.value === 'boolean'
  ) {
    a = a.value ? 1 : 0;
    b = b.value ? 1 : 0;
  }

  if (typeof a === 'boolean' && typeof b === 'boolean' && localize) {
    a = formatBoolean({ localize, value: a });
    b = formatBoolean({ localize, value: b });
    const collator: Intl.Collator = props.collator || new Intl.Collator(formatLocale);
    return collator.compare(a, b);
  }

  return a < b ? -1 : a > b ? 1 : 0;
};

export const sortArray = (props: {
  formatLocale: string;
  array: any[];
  rootPropertyName?: string;
  propertyName?: string;
  descending?: boolean;
  caseSensitive?: boolean;
  localize?: Localizer;
}) => {
  const {
    array,
    rootPropertyName,
    propertyName,
    descending,
    caseSensitive,
    localize,
    formatLocale,
  } = props;
  const collator: Intl.Collator = new Intl.Collator(formatLocale, {
    sensitivity: caseSensitive ? 'case' : 'base',
  });
  array.sort(function (a, b) {
    if (rootPropertyName) {
      a = a && a[rootPropertyName] ? a[rootPropertyName] : (null as never);
      b = b && b[rootPropertyName] ? b[rootPropertyName] : (null as never);
    }
    if (propertyName) {
      if (a) a = a[propertyName];
      if (b) b = b[propertyName];
    }
    const rv = compare({
      formatLocale,
      value1: a,
      value2: b,
      collator,
      localize,
    });
    return descending ? rv * -1 : rv;
  });
};

/**
 * Produce an array of ints where each int represents the sorted position of the row from the input view at the corresponding index
 * @param view - A matrix of values, where each column in the matrix has a corresponding sort direction in the directions list
 * @param directions - A list of TableColumnSortDirection values.  The view is sorted by the first direction first, and any "ties" are resolved recursively,
 * by comparing the values in the columns that correspond with the subsequent directions.
 */
export const generateSortOffsets = (props: {
  view: Array<Array<any>>;
  formatLocale: string;
  directions?: Array<Omit<TableColumnSortDirection, 'data'>>;
}): Array<number> => {
  const { view, formatLocale } = props;
  let directions = props.directions;
  const colsLength: number = view.length;
  if (colsLength === 0) {
    return [];
  }
  const rowsLength: number = view[0].length;
  if (rowsLength === 1) {
    return [0];
  }
  if (!directions) {
    directions = [];
  }
  // if the directions list doesn't have an entry per view column, default to just repeating the last value in the
  // directions list for each additional column
  const origDirectionsLength: number = directions.length;
  if (origDirectionsLength < colsLength) {
    for (let d = 0; d < colsLength - origDirectionsLength; d++) {
      let previousDirection: Omit<TableColumnSortDirection, 'data'> | undefined =
        directions[origDirectionsLength + d - 1];
      if (previousDirection === undefined) {
        // if directions was empty use an ascending sort by default
        previousDirection = 'ascending';
      }
      directions[origDirectionsLength + d] = previousDirection;
    }
  }
  const collator: Intl.Collator = new Intl.Collator(formatLocale, { sensitivity: 'case' });
  const sortedIndices: Array<number> = new Array(rowsLength);
  for (let i = 0; i < rowsLength; i++) {
    for (let r = 0; r < rowsLength - 1; r++) {
      let comp = 0;
      if (sortedIndices[r] === undefined) {
        sortedIndices[r] = r;
      }
      if (sortedIndices[r + 1] === undefined) {
        sortedIndices[r + 1] = r + 1;
      }
      const ix: number = sortedIndices[r];
      const nx: number = sortedIndices[r + 1];
      for (let c = 0; c < colsLength; c++) {
        const curValue: any = view[c][ix];
        const nextValue: any = view[c][nx];
        comp = compare({
          formatLocale,
          value1: curValue,
          value2: nextValue,
          trimStringValues: false,
          collator,
        });
        if (directions[c] === 'descending') {
          comp *= -1;
        }
        if (comp > 0) {
          sortedIndices[r] = nx;
          sortedIndices[r + 1] = ix;
        }
        if (comp !== 0) {
          break;
        }
      }
    }
  }
  return sortedIndices;
};
/**
 * Sorts the given array inline based on the value of array[n][property]
 * @param array
 * @param property
 */

export const sortOn = (props: {
  array: Array<any>;
  property: string;
  comparator?: (a: any, b: any) => number;
}): void => {
  const { array, property } = props;
  let comparator = props.comparator;
  if (!array || !property) {
    return;
  }
  comparator =
    comparator ||
    ((a, b): number => {
      if (typeof a === 'string') {
        return a.localeCompare(b);
      } else {
        if (a === b) {
          return 0;
        } else if (a > b) {
          return 1;
        }
        return -1;
      }
    });
  array.sort((a, b): number => {
    if (!a || isNullish(a[property])) {
      if (!b || isNullish(b[property])) {
        return 0;
      }
      return 1;
    }
    if (!b || isNullish(b[property])) {
      return -1;
    }
    return comparator!(a[property], b[property]);
  });
};
